/**
 * Pharmascience — inline FORMAT validation on wp-login.php.
 *
 * Catches the mistakes that do not need a server to detect: an empty field, or
 * an address that cannot be an address. Everything else — whether the
 * credentials are CORRECT — stays server-side, deliberately (see the security
 * note below).
 *
 * 🔴 THIS NEVER BLOCKS A CORRECT SUBMISSION AND NEVER CHECKS CREDENTIALS.
 *
 * The operator asked (2026-08-19) for inline error messaging "for incorrect
 * credentials without refreshing, if any security concern, hold it". The
 * credential half is HELD. Three reasons, all specific to this site:
 *
 *   1. USER ENUMERATION. Any response distinguishing "wrong password" from
 *      "no such account" confirms which email addresses hold accounts. This
 *      site's users are identifiable opioid-prescribing HCPs, so mere
 *      membership is itself sensitive. Flag F-01's shipped fix was verified
 *      for a NON-ENUMERATING message; an AJAX oracle would undo it.
 *   2. LOCKOUT BYPASS. Per-IP and per-account failure counting is enforced in
 *      `bnrmp-2fa-lockout.php` at `authenticate` priority 21. A parallel
 *      guess channel that does not run `authenticate` is an uncounted one.
 *   3. It buys one round trip on the ONLY path where the user is already
 *      wrong. Typos — the common case — are caught here with no request.
 *
 * Progressive enhancement, hard requirement: this file runs on the screen that
 * authenticates people. `novalidate` is set from JS, so if this bundle fails to
 * load the browser's own constraint UI still applies and the form still posts.
 * Nothing here can prevent a sign-in.
 */

const ERROR_CLASS = 'bnrmp-field-error';

/* Deliberately permissive: "has an @ with something either side, no spaces".
 * Server-side `is_email()` remains the only authority — this exists to catch
 * "ringo@" and "ringo clearoute.com", not to adjudicate RFC 5322. A stricter
 * pattern here would reject valid addresses, which is a worse failure than
 * passing a bad one to a server that checks properly.
 * ponytail: shape check only; is_email() is the real gate. */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function messageFor(field) {
  const value = field.input.value.trim();

  if (value === '') {
    return field.emptyMessage;
  }

  /* Only shape-check when it LOOKS like an email attempt. The username field
   * legitimately accepts a WP username too (core's own label says "Username or
   * Email"), so a value with no "@" is not an error here. */
  if (field.checkEmailShape && value.includes('@') && !EMAIL_SHAPE.test(value)) {
    return field.invalidMessage;
  }

  return '';
}

function clearError(field) {
  field.input.removeAttribute('aria-invalid');
  if (field.node) {
    field.node.textContent = '';
    field.node.hidden = true;
  }
}

function showError(field, message) {
  field.input.setAttribute('aria-invalid', 'true');

  if (!field.node) {
    const node = document.createElement('p');
    node.className = ERROR_CLASS;
    node.id = `${field.input.id}-bnrmp-error`;
    /* `alert` announces the text when it changes without moving focus — focus
     * must stay in the field the person is typing in. */
    node.setAttribute('role', 'alert');
    field.input.insertAdjacentElement('afterend', node);
    field.node = node;

    /* Append rather than overwrite: core already points password fields at
     * their own description in some flows, and clobbering it would silence
     * that for screen readers. */
    const described = field.input.getAttribute('aria-describedby');
    field.input.setAttribute(
      'aria-describedby',
      described ? `${described} ${node.id}` : node.id
    );
  }

  field.node.hidden = false;
  field.node.textContent = message;
}

function validate(field) {
  const message = messageFor(field);
  if (message) {
    showError(field, message);
    return false;
  }
  clearError(field);
  return true;
}

export default function initLoginInlineValidate(root = document) {
  /* Every form this applies to, keyed by the input ids core emits. `user_pass`
   * is absent on the lost-password screen, which is why each field is resolved
   * independently instead of assuming a shape. */
  const form = root.querySelector('#loginform, #lostpasswordform');
  if (!form) {
    return;
  }

  const strings = (window.bnrmpLoginI18n && typeof window.bnrmpLoginI18n === 'object')
    ? window.bnrmpLoginI18n
    : {};

  const candidates = [
    {
      input: form.querySelector('#user_login'),
      checkEmailShape: true,
      emptyMessage: strings.emptyLogin || 'Enter your username or email address.',
      invalidMessage: strings.invalidEmail || 'Enter a valid email address.',
    },
    {
      input: form.querySelector('#user_pass'),
      checkEmailShape: false,
      emptyMessage: strings.emptyPass || 'Enter your password.',
      invalidMessage: '',
    },
  ];

  const fields = candidates.filter((f) => f.input);
  if (!fields.length) {
    return;
  }

  /* Set from JS, not markup: with the bundle absent the browser's native
   * required-field UI stays in charge rather than nothing at all. */
  form.setAttribute('novalidate', 'novalidate');

  fields.forEach((field) => {
    field.node = null;

    /* Validate on blur, never on keystroke — flagging "invalid email" while
     * someone is halfway through typing one is noise, not help. */
    field.input.addEventListener('blur', () => validate(field));

    /* Clear as soon as they start fixing it; do not re-validate mid-edit. */
    field.input.addEventListener('input', () => {
      if (field.input.hasAttribute('aria-invalid')) {
        clearError(field);
      }
    });
  });

  form.addEventListener('submit', (event) => {
    /* Evaluate every field before deciding, so all problems surface at once
     * rather than one reload at a time. `.filter()` not `.every()` — the
     * latter short-circuits and would leave the second field unmarked. */
    const failed = fields.filter((field) => !validate(field));

    if (!failed.length) {
      return; // Let it post. The server decides the credentials.
    }

    event.preventDefault();
    failed[0].input.focus();
  });
}
